import { useState } from 'react'
import { BadgeCheck, Ban, ExternalLink, Hourglass, Send, Undo2, Undo, XCircle } from 'lucide-react'
import { Button, DetailSection, buttonClass, ButtonGlyph } from '../../../core/index.js'
import type { FeedbackAdminApi } from '../../adminApi.js'
import type { ChangeRequestView } from '../../types.js'
import { isClosedChange, shortSha, WAITING_ON_ROOT } from './changeText.js'
import { AcceptDialog, ApproveDialog } from './GateDialogs.js'
import { PromptPanel, PullRequestForm } from './PromptPanel.js'
import { ReasonDialog } from './StepDialog.js'

type AdminApi = Pick<
  FeedbackAdminApi,
  'returnChange' | 'approveChange' | 'changePrompt' | 'recordPullRequest' | 'sendBack' | 'acceptChange' | 'rejectChange' | 'cancelChange'
>

type Dialog = 'return' | 'approve' | 'send_back' | 'accept' | 'reject' | 'cancel'

const WAITING_TEXT: Partial<Record<ChangeRequestView['status'], string>> = {
  proposed: 'Waiting on the root admin to approve it or return it.',
  approved: 'Waiting on the root admin to run the approved prompt and record the pull request.',
  ready_for_review: 'Waiting on the root admin to review the pull request.',
}

function PullRequestLine({ change }: { change: ChangeRequestView }) {
  const pullRequest = change.pullRequest
  if (!pullRequest) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink">
      <a href={pullRequest.url} target="_blank" rel="noreferrer" className={buttonClass({ variant: 'secondary', size: 'sm' })}>
        <ButtonGlyph icon={ExternalLink} size="sm" />
        Pull request{pullRequest.number !== null ? ` #${pullRequest.number}` : ''}
      </a>
      <span>
        Head <span className="font-mono select-all">{pullRequest.headSha}</span>
      </span>
      {pullRequest.branch && (
        <span>
          Branch <span className="font-mono">{pullRequest.branch}</span>
        </span>
      )}
    </div>
  )
}

/**
 * The steps only the root takes, by where the change stands. Every other
 * admin sees the same page with "Waiting on the root admin" instead.
 */
export function RootActions({ api, change, isRoot }: { api: AdminApi; change: ChangeRequestView; isRoot: boolean }) {
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const close = () => setDialog(null)
  const id = change.changeRequestId
  if (isClosedChange(change)) {
    return null
  }
  if (!isRoot) {
    const waiting = WAITING_TEXT[change.status]
    return waiting ? (
      <div className="flex flex-col gap-3">
        <PullRequestLine change={change} />
        <p className="flex items-center gap-1.5 text-xs font-bold text-ink-mid">
          <Hourglass aria-hidden="true" className="h-3.5 w-3.5" />
          {waiting}
        </p>
      </div>
    ) : null
  }

  return (
    <div className="flex flex-col gap-4">
      {change.status === 'approved' && <PromptPanel api={api} changeRequestId={id} />}
      {change.status === 'ready_for_review' && <PullRequestLine change={change} />}
      {(change.status === 'approved' || change.status === 'ready_for_review') && <PullRequestForm key={change.version} api={api} change={change} />}

      <DetailSection title="Root admin">
        <div className="flex flex-wrap gap-2">
          {change.status === 'proposed' && (
            <>
              <Button icon={Send} size="sm" onClick={() => setDialog('approve')}>
                Approve and send
              </Button>
              <Button icon={Undo2} variant="secondary" size="sm" onClick={() => setDialog('return')}>
                Return
              </Button>
            </>
          )}
          {change.status === 'ready_for_review' && (
            <>
              <Button icon={BadgeCheck} size="sm" onClick={() => setDialog('accept')}>
                Accept at {shortSha(change.pullRequest?.headSha ?? '')}
              </Button>
              <Button icon={Undo} variant="secondary" size="sm" onClick={() => setDialog('send_back')}>
                Send back
              </Button>
              <Button icon={XCircle} variant="secondary" size="sm" onClick={() => setDialog('reject')}>
                Reject
              </Button>
            </>
          )}
          <Button icon={Ban} variant="secondary" size="sm" onClick={() => setDialog('cancel')}>
            Cancel change
          </Button>
        </div>
        {WAITING_ON_ROOT.includes(change.status) && change.status !== 'approved' && <p className="mt-2 text-[11px] text-ink-mid">This is a gate only you can pass.</p>}
      </DetailSection>

      {dialog === 'approve' && <ApproveDialog api={api} change={change} onClose={close} />}
      {dialog === 'accept' && <AcceptDialog api={api} change={change} onClose={close} />}
      {dialog === 'return' && (
        <ReasonDialog title="Return to draft" label="What needs changing?" placeholder="The admin writing it will see this." confirmLabel="Return" confirmIcon={Undo2} run={(reason) => api.returnChange(id, reason)} onClose={close} />
      )}
      {dialog === 'send_back' && (
        <ReasonDialog title="Send back" label="Notes for the next attempt" confirmLabel="Send back" confirmIcon={Undo} run={(notes) => api.sendBack(id, notes)} onClose={close}>
          <p className="text-xs text-ink-mid">The change goes back to approved, with the same prompt; record the new pull request or commit when it comes back.</p>
        </ReasonDialog>
      )}
      {dialog === 'reject' && (
        <ReasonDialog title="Reject the pull request" label="Why is it rejected?" confirmLabel="Reject" confirmIcon={XCircle} run={(reason) => api.rejectChange(id, reason)} onClose={close}>
          <p className="text-xs text-ink-mid">The change closes and its feedback is free to be developed again.</p>
        </ReasonDialog>
      )}
      {dialog === 'cancel' && (
        <ReasonDialog title="Cancel the change" label="Why is it being stopped?" confirmLabel="Cancel change" confirmIcon={Ban} run={(reason) => api.cancelChange(id, reason)} onClose={close}>
          <p className="text-xs text-ink-mid">The change closes and its feedback is free to be developed again.</p>
        </ReasonDialog>
      )}
    </div>
  )
}
